import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Question, FileText, UploadSimple, MagicWand,
  Sparkle, Palette, DownloadSimple, ShieldCheck, TextAlignLeft, Ruler,
} from "@phosphor-icons/react";
import Logo from "@/components/Logo";

const Steps = [
  {
    n: "01",
    title: "Start fresh or load the sample",
    body: "Open the Builder and either start from a blank form, click Load sample to explore with real data, or click Import to upload an existing résumé (.pdf or .docx) that gets parsed into the form for you.",
    icon: FileText,
  },
  {
    n: "02",
    title: "Fill in your details",
    body: "Walk through Personal, Summary, Experience, Education, Projects, Skills and Certifications. Every keystroke updates the live preview instantly. Add or remove entries with the + and − controls beside each item.",
    icon: TextAlignLeft,
  },
  {
    n: "03",
    title: "Polish with AI",
    body: "Tap the red spark next to any bullet or the Summary field to have Claude rewrite it into recruiter-ready copy. Facts stay yours — only the phrasing improves. Click Match JD to paste a job description and let AI tailor the résumé and suggest missing skills.",
    icon: Sparkle,
  },
  {
    n: "04",
    title: "Pick a template",
    body: "Nine editorial templates: Modern, Classic, Minimal, Creative, Executive, ATS Plain, ATS Compact, Two-Column, Photo Header. Switch anytime — your content survives the change.",
    icon: Palette,
  },
  {
    n: "05",
    title: "Fine-tune the layout",
    body: "Choose Page size (A4 or US Letter), Density (Comfortable or Compact) and Font size (8 pt – 12 pt). Hit Auto-fit to one page and the builder tightens density and font to squeeze everything onto page 1. If content genuinely doesn't fit, the overflow stays on page 2 — nothing is lost.",
    icon: Ruler,
  },
  {
    n: "06",
    title: "Download your PDF",
    body: "Click Download PDF. The browser's native print dialog opens — pick 'Save as PDF' as the destination. Tip: turn off Headers and footers in the print options for the cleanest output.",
    icon: DownloadSimple,
  },
];

const Faqs = [
  {
    q: "Do you store my data?",
    a: "No. Your résumé lives only in your current browser session. Nothing is written to a server database. When you close the tab, everything is wiped. Save the PDF before you leave.",
  },
  {
    q: "Do I need an account?",
    a: "No login, no signup. Open the builder and start writing.",
  },
  {
    q: "How does AI polish work?",
    a: "Your bullet or summary text is sent to a large language model (Claude, via Emergent) with a strict instruction to rewrite for tone and clarity without inventing facts. You always see the original and approve or reject the rewrite.",
  },
  {
    q: "What are ATS templates?",
    a: "ATS Plain and ATS Compact are single-column, no-graphics layouts optimized for Applicant Tracking Systems that parse résumés into text. Use them when you're applying through a large company portal.",
  },
  {
    q: "Why is Ctrl+P and right-click disabled?",
    a: "To protect your draft from casual screenshots or unintended printing while you're editing. The Download PDF button still works — it triggers the print flow programmatically and drops the block for that one action.",
  },
  {
    q: "Why does my PDF span 2 pages?",
    a: "The résumé is longer than a single physical page. The pagination logic keeps each job entry intact — an entry never splits across pages. Try Auto-fit to one page, or move to Compact density and 9 pt font.",
  },
  {
    q: "Can I upload a photo?",
    a: "Yes, but only the Two-Column and Photo Header templates render one. Upload a JPG or PNG under 2 MB. The photo stays local to your browser.",
  },
  {
    q: "How do I get in touch?",
    a: "Write to contact@myresumedesign.com — support requests, feedback and refund questions are all handled at that address.",
  },
];

export default function Help() {
  return (
    <div className="min-h-screen bg-white text-neutral-950 font-sans" data-testid="help-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-black" data-testid="help-back-home">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <div className="h-4 w-px bg-black/20" />
            <Logo />
          </div>
          <Link to="/builder" className="btn-primary text-xs" data-testid="help-cta-build">
            Open builder
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-black/5">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="label-mono mb-6">
            <span className="inline-block w-6 h-px bg-black/40 align-middle mr-2" />
            Help / Guide
          </div>
          <h1 className="font-heading font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
            Everything you need
            <br />
            to <span className="shimmer-text">use the tool.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base lg:text-lg text-neutral-600 leading-relaxed">
            A quick tour of the six steps that take you from a blank page to a recruiter-ready PDF,
            plus answers to the questions we get most often.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="border-b border-black/5">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="label-mono mb-8">01 / How to use</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="help-steps">
            {Steps.map((s) => (
              <div key={s.n} className="border border-black/10 p-6 hover:border-black/40 transition-colors" data-testid={`help-step-${s.n}`}>
                <div className="flex items-start justify-between">
                  <s.icon size={22} weight="duotone" className="text-[#2400FF]" />
                  <div className="font-mono text-xs text-neutral-400">{s.n}</div>
                </div>
                <div className="mt-4 font-heading font-semibold text-lg tracking-tight">{s.title}</div>
                <div className="mt-2 text-sm text-neutral-600 leading-relaxed">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature cheatsheet */}
      <section className="border-b border-black/5 bg-neutral-50/50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="label-mono mb-8">02 / Feature cheatsheet</div>
          <div className="space-y-0 border-t border-black/10">
            {[
              { k: "Import", d: "Parses an existing .pdf or .docx résumé into the form.", i: UploadSimple },
              { k: "Match JD", d: "Paste a job description; AI tailors experience wording and recommends the best template.", i: MagicWand },
              { k: "AI polish (red spark)", d: "Rewrites a single bullet or the summary. You always approve before it replaces your text.", i: Sparkle },
              { k: "Auto-fit to one page", d: "Sets density to compact and steps the font from 10 pt down until the résumé fits on one page. If content genuinely exceeds one page, the extra content flows to page 2 — never truncated.", i: Ruler },
              { k: "Page size / Density / Font size", d: "A4 vs US Letter, Comfortable vs Compact, 8 pt – 12 pt. Changes apply live to the preview and the exported PDF.", i: Palette },
              { k: "Download PDF", d: "Opens the native print dialog. Save as PDF from there. Turn off Headers/footers for the cleanest output.", i: DownloadSimple },
              { k: "Session-only storage", d: "No account, no server database. Everything lives in your browser tab and is wiped when you close it.", i: ShieldCheck },
            ].map((row) => (
              <div key={row.k} className="grid grid-cols-12 gap-4 py-6 border-b border-black/10">
                <div className="col-span-12 md:col-span-1 flex items-start">
                  <row.i size={20} weight="duotone" className="text-[#2400FF]" />
                </div>
                <div className="col-span-12 md:col-span-3 font-heading font-semibold text-base">{row.k}</div>
                <div className="col-span-12 md:col-span-8 text-sm text-neutral-600 leading-relaxed">{row.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-black/5">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="label-mono mb-8">03 / Frequently asked</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-8" data-testid="help-faqs">
            {Faqs.map((f, i) => (
              <div key={f.q} data-testid={`help-faq-${i}`}>
                <div className="flex items-start gap-2">
                  <Question size={18} weight="duotone" className="text-[#FF2A00] mt-0.5 shrink-0" />
                  <div className="font-heading font-semibold text-base tracking-tight">{f.q}</div>
                </div>
                <div className="mt-2 pl-6 text-sm text-neutral-600 leading-relaxed">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-neutral-950 text-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 lg:col-span-8">
              <h2 className="font-heading font-black tracking-tighter text-4xl lg:text-6xl leading-[0.95]">
                Ready to write?
              </h2>
              <p className="mt-4 text-neutral-400 max-w-lg">
                Open the builder. Load the sample if you'd like a head start. Ship a PDF in the next ten minutes.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
              <Link
                to="/builder"
                className="inline-flex items-center gap-3 bg-white text-black px-6 py-4 text-sm font-medium rounded-sm hover:-translate-y-[1px] transition-all"
                data-testid="help-footer-cta"
              >
                Open the builder
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-black/10 py-8">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-neutral-500">
          <div className="font-mono">© 2026 MyResumeDesign</div>
          <div className="flex flex-wrap items-center gap-6 font-mono uppercase tracking-widest text-xs">
            <a href="mailto:contact@myresumedesign.com" className="normal-case hover:text-black" data-testid="help-footer-contact">contact@myresumedesign.com</a>
            <Link to="/help" className="hover:text-black">Help</Link>
            <Link to="/privacy" className="hover:text-black">Privacy</Link>
            <Link to="/terms" className="hover:text-black">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
